import React, {
  useState, useEffect, useContext
} from 'react';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { string, number } from 'prop-types';
import {
  bool as boolType,
  client,
  params,
  arrayOf as arrayOfType,
  number as numberType,
  shape,
  string as stringType,
  useDataModel,
  useLazyDataModel,
  extend,
  QueryProvider
} from '@thd-nucleus/data-sources';
import { Button } from '@one-thd/sui-atomic-components';
import { Drawer, DrawerItem, DrawerHeader } from '@thd-olt-component-react/drawer';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import { ProductPodUtils } from '@thd-olt-component-react/product-pod';

import { Loading } from './shared/Loading';
import { SuccessDrawer } from './drawers/SuccessDrawer';
import { ErrorDrawer } from './drawers/ErrorDrawer';
import { AddToQuoteDrawer } from './drawers/AddToQuoteDrawer';
import { CreateQuoteDrawer } from './drawers/CreateQuoteDrawer';
import { PriceChangeWarningDrawer } from './drawers/PriceChangeWarningDrawer';
import { SignInDrawer } from './drawers/SignInDrawer';

import {
  DRAWERS,
  GENERIC_ERROR,
  MOBILE_CHANNEL,
  DEFAULT_SELECTED_QUOTE,
  QUOTE_ACTIVE_STATUS,
  QUOTE_PENDING_STATUS,
  ADMIN_ROLE
} from './shared/Constants';

const customerInfoCache = {
  svocId: null,
  userId: null
};

const AddToQuote = ({
  itemId,
  quantity,
}) => {
  const { channel, deliveryZip } = useContext(ExperienceContext);
  const store = useStore();
  const [drawerShowing, setDrawerShowing] = useState(false);
  const [error, setError] = useState(null);
  const [quoteName, setQuoteName] = useState('');
  const [successful, setSuccess] = useState(false); // used because of refetch causing loading
  const [selectedQuote, setSelectedQuote] = useState(DEFAULT_SELECTED_QUOTE);
  const [quoteList, setQuoteList] = useState([]);
  const [quotesPageSize, setQuotesPageSize] = useState(20);
  const [showLoadMore, setShowLoadMore] = useState(false);

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to-quote.ready'); }, []);

  const { data, error: errorProduct, variables: variablesProduct } = useDataModel('product', {
    variables: {
      itemId,
    },
    skip: !itemId,
  });

  const product = data?.product;
  const { identifiers, media, pricing } = product || {};
  const { brandName, productLabel } = identifiers || {};
  const mediaImages = media?.images || [];
  const mainImage = mediaImages[0] || '';

  const disabled = !podFulFillmentUtils.isATCEnabled(product) || podFulFillmentUtils.isAppliance(product);
  const fulfillmentMethod = podFulFillmentUtils.getFulfillmentMethod(product);
  const fulfillmentLocation = ProductPodUtils.getFulfillmentLocation({
    fulfillmentMethod, store, product, deliveryZip
  });

  if (!customerInfoCache.svocID) {
    if (typeof window !== 'undefined') {
      const { svocID, userID, role } = window.THDCustomer?.default;
      customerInfoCache.svocId = svocID;
      customerInfoCache.userId = userID;
      customerInfoCache.role = role;
    }
  }

  const variables = {
    svocId: customerInfoCache.svocId,
    pageSize: quotesPageSize,
    userId: customerInfoCache.userId
  };

  /**
   * This component will be consumed in different places, like buybox on PIP,
   * and product pod in BuyItAgain and Lists. There are differences
   * in the `QueryProvider` configuration between these places that change
   * `fetchPolicy`, so we need to set one explicity that works for us no matter
   * where we are.
   *
   * For example, when on a PLP-like page, the `QueryProvider` will typically be
   * configured with dataSource prop equal to `searchNav`, which will override
   * the default `fetchPolicy` from `cache-first` to `cache-only`. This does
   * not work for our `getQuoteList` call below since we need to make the
   * call at least once to get data, hence the explicit fetchPolicy=cache-first
   * to check cache first, then make a network call if necessary.
   */
  const [
    getAllQuotes,
    { data: quoteData, loading: quoteLoading, error: quoteError, variables: quoteVariables }
  ] = useLazyDataModel('getQuoteList', {
    variables,
    fetchPolicy: 'cache-first'
  });

  const toggleDrawer = () => {
    if (!quoteData && !quoteError) {
      getAllQuotes();
    }
    setDrawerShowing(!drawerShowing);
  };

  const { totalNbrOfQuotes } = quoteData?.getQuoteList || {};

  useEffect(() => {
    if (drawerShowing && quoteData && (!successful && !quoteError)) {
      if (totalNbrOfQuotes > 0) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.addToQuoteOverlay');
      } else if (totalNbrOfQuotes === 0) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.createAQuoteOverlay');
      }
    }
  }, [quoteData, drawerShowing]);

  const getInitialItem = () => {
    if (quoteError && /AUTH/.test(quoteError.message)) return DRAWERS.SIGN_IN_WARNING;
    if (!quoteLoading && quoteError) return DRAWERS.ERROR;
    if (successful) return DRAWERS.SUCCESS;
    return !quoteLoading && quoteList?.length > 0 ? DRAWERS.ADD_TO_QUOTE : DRAWERS.CREATE_QUOTE;
  };

  const getErrorMessage = () => {
    if (!error) return '';
    const { message, graphQLErrors } = error;

    // check is API error message
    if (!message || message === 'null') {
      if (graphQLErrors.length) {
        const { extensions } = graphQLErrors[0];
        const { errorData } = extensions;
        if (errorData.length) {
          const { errors } = errorData[0];
          if (errors.length) return errors[0].description;
        }
      }
    }

    // Check if generic from server without helpful info
    if (/(50|51|40|41)/.test(message)) return GENERIC_ERROR;

    // Otherwise return the message
    return error.message;
  };

  const isAccessTypeValid = (accessType) => {
    // hide if accessType on 1 or 2, show if null or not 1 or 2
    return !accessType || (accessType && (accessType !== 1 && accessType !== 2));
  };

  const statusIsValid = (status) => {
    const upperCased = status.toUpperCase();
    return upperCased === QUOTE_ACTIVE_STATUS || upperCased === QUOTE_PENDING_STATUS;
  };

  const shouldShowItem = ({ status, accessType }) => {
    return statusIsValid(status) && isAccessTypeValid(accessType);
  };

  useEffect(() => {
    if (quotesPageSize > 20) {
      getAllQuotes();
    }
    const quotes = quoteData?.getQuoteList?.quotes || [];
    const filteredQuotes = quotes.filter(({ status, accessType }) => {
      return shouldShowItem({ status, accessType });
    }) || [];
    setQuoteList(filteredQuotes);
    const index = quotes.length;
    if (quotes[index - 1]?.status === 'Expired' || totalNbrOfQuotes === quoteList?.length) {
      setShowLoadMore(false);
    } else {
      setShowLoadMore(true);
    }
  }, [quotesPageSize]);

  useEffect(() => {
    const quotes = quoteData?.getQuoteList?.quotes || [];
    const filteredQuotes = quotes.filter(({ status, accessType }) => {
      return shouldShowItem({ status, accessType });
    }) || [];
    if (drawerShowing && !quoteList.length && filteredQuotes.length) {
      setQuoteList(filteredQuotes);
    }
    const index = quotes.length - 1;
    if (quotes[index]?.status === 'Expired' || totalNbrOfQuotes === quoteList?.length) {
      setShowLoadMore(false);
    } else {
      setShowLoadMore(true);
    }
  }, [drawerShowing, quoteList, quoteData]);

  return (
    <>
      <Button
        variant="secondary"
        fullWidth
        onClick={toggleDrawer}
        disabled={disabled}
      >
        Add to Quote
      </Button>
      {data?.product && (
        <QueryProvider
          cacheKey="add-to-quote-drawer"
          defaultVariables={{ svocId: customerInfoCache.svocId }}
        >
          <Drawer
            open={drawerShowing}
            onClose={() => {
              setDrawerShowing(false);
              setSelectedQuote(DEFAULT_SELECTED_QUOTE);
              setQuoteList([]);
              setQuotesPageSize(20);
            }}
            initialItem={quoteLoading ? DRAWERS.LOADING : getInitialItem()}
            isMobile={channel === MOBILE_CHANNEL}
            position="right"
            hideMask={channel === MOBILE_CHANNEL}
            maxSize={channel === MOBILE_CHANNEL ? 100 : 400}
          >
            <DrawerHeader title="Add to Quote" />
            <DrawerItem name={DRAWERS.LOADING}>
              <Loading active />
            </DrawerItem>
            <DrawerItem name={DRAWERS.ADD_TO_QUOTE}>
              <AddToQuoteDrawer
                productLabel={productLabel}
                brandName={brandName}
                mainImage={mainImage}
                quoteList={quoteList}
                setSelectedQuote={setSelectedQuote}
                selectedQuote={selectedQuote}
                showLoadMore={showLoadMore}
                quotesPageSize={quotesPageSize}
                setQuotesPageSize={setQuotesPageSize}
              />
            </DrawerItem>
            <DrawerItem name={DRAWERS.CREATE_QUOTE}>
              <CreateQuoteDrawer
                itemId={itemId}
                customerInfo={customerInfoCache}
                productLabel={productLabel}
                price={pricing?.value}
                brandName={brandName}
                mainImage={mainImage}
                quantity={quantity}
                fulfillmentMethod={fulfillmentMethod}
                fulfillmentLocation={fulfillmentLocation}
                localStoreId={store.storeId}
                setError={setError}
                setQuoteName={setQuoteName}
                setSuccess={setSuccess}
              />
            </DrawerItem>
            <DrawerItem name={DRAWERS.PRICE_CHANGE_WARNING}>
              <PriceChangeWarningDrawer
                itemId={itemId}
                quantity={quantity}
                price={pricing?.value}
                fulfillmentLocation={fulfillmentLocation}
                fulfillmentMethod={fulfillmentMethod}
                localStoreId={store.storeId}
                customerInfo={customerInfoCache}
                setQuoteName={setQuoteName}
                setError={setError}
                selectedQuote={selectedQuote}
              />
            </DrawerItem>
            <DrawerItem name={DRAWERS.SIGN_IN_WARNING}>
              <SignInDrawer />
            </DrawerItem>
            <DrawerItem name={DRAWERS.SUCCESS}>
              <SuccessDrawer
                quoteName={quoteName}
                setQuoteName={setQuoteName}
                setSelectedQuote={setSelectedQuote}
                setSuccess={setSuccess}
              />
            </DrawerItem>
            <DrawerItem name={DRAWERS.ERROR}>
              <ErrorDrawer message={getErrorMessage()} />
            </DrawerItem>
          </Drawer>
        </QueryProvider>
      )}
    </>
  );
};

AddToQuote.propTypes = {
  itemId: string.isRequired,
  quantity: number.isRequired
};

AddToQuote.dataModel = extend(
  {
    product: params({
      itemId: stringType().isRequired(),
    }).shape({
      itemId: stringType(),
      dataSources: stringType(),
      identifiers: shape({
        productLabel: stringType(),
        brandName: stringType()
      }),
      media: shape({
        images: arrayOfType(
          shape({
            url: stringType(),
            sizes: arrayOfType(stringType()),
          }),
        ),
      }),
      pricing: params({ storeId: stringType() }).shape({
        value: numberType({ float: true }),
      }),
      fulfillment: client(params({ storeId: stringType(), zipCode: stringType() }).shape({
        anchorStoreStatus: boolType(),
        anchorStoreStatusType: stringType(),
        backordered: boolType(),
        backorderedShipDate: stringType(),
        bossExcludedShipStates: stringType(),
        excludedShipStates: stringType(),
        seasonStatusEligible: boolType(),
        fulfillmentOptions: arrayOfType(shape({
          type: stringType(),
          fulfillable: boolType(),
          services: arrayOfType(shape({
            deliveryTimeline: stringType(),
            deliveryDates: shape({
              startDate: stringType(),
              endDate: stringType(),
            }),
            deliveryCharge: stringType(),
            dynamicEta: shape({
              hours: stringType(),
              minutes: stringType()
            }),
            hasFreeShipping: boolType(),
            freeDeliveryThreshold: numberType({ float: true }),
            locations: arrayOfType(shape({
              curbsidePickupFlag: boolType(),
              isBuyInStoreCheckNearBy: boolType(),
              distance: numberType({ float: true }),
              inventory: shape({
                isOutOfStock: boolType(),
                isInStock: boolType(),
                isLimitedQuantity: boolType(),
                isUnavailable: boolType(),
                quantity: numberType(),
                maxAllowedBopisQty: numberType(),
                minAllowedBopisQty: numberType()
              }),
              isAnchor: boolType(),
              locationId: stringType(),
              state: stringType(),
              storeName: stringType(),
              storePhone: stringType(),
              type: stringType(),
            })),
            type: stringType(),
            totalCharge: numberType({ float: true }),
          }))
        })),
        onlineStoreStatus: boolType(),
        onlineStoreStatusType: stringType(),
      }))
    })
  },
  {
    getQuoteList: params({
      svocId: stringType().isRequired(),
      userId: stringType(),
      pageNo: stringType(),
      pageSize: stringType(),
    }).shape({
      userId: stringType(),
      customerAccountId: stringType(),
      totalNbrOfQuotes: numberType({ int: true }),
      totalNbrOfPages: numberType({ int: true }),
      quotes: arrayOfType(
        shape({
          name: stringType(),
          status: stringType(),
          quoteId: stringType(),
          userId: stringType(),
          accessType: numberType(),
        }),
      ),
      errorData: arrayOfType(
        shape({
          errors: arrayOfType(
            shape({
              id: stringType(),
              description: stringType(),
              submessage: stringType(),
              errorCode: stringType(),
              errorProperties: shape({
                errorProperty: shape({
                  name: stringType(),
                  value: stringType(),
                }),
              }),
            }),
          ),
          httpStatus: stringType(),
          userId: stringType(),
          customerAccountId: stringType(),
          correlationId: stringType(),
          correlationType: stringType(),
        }),
      ),
    }),
  },
  CreateQuoteDrawer,
  PriceChangeWarningDrawer,
);

export { AddToQuote };
